import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Star, Trash, Plus, PencilSimple } from "@phosphor-icons/react";

const empty = { author: "", location: "", rating: 5, text: "", is_sample: false, published: true };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...t }); setEditId(t.id); setOpen(true); };

  const save = async () => {
    if (!form.author || !form.text) { toast.error("Author and text are required"); return; }
    const payload = { author: form.author, location: form.location, rating: Number(form.rating),
      text: form.text, is_sample: form.is_sample, published: form.published };
    try {
      if (editId) await api.patch(`/admin/testimonials/${editId}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load(); toast.success("Deleted");
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Testimonials</h1>
        <Button onClick={openNew} data-testid="add-testimonial" className="rounded-full bg-brand-purple text-white hover:bg-brand-purple2">
          <Plus size={18} className="mr-1.5" /> Add Review
        </Button>
      </div>
      <p className="mt-1 text-sm text-slate-500">Add real customer reviews. Only published reviews appear on the website.</p>

      <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex gap-0.5 text-amber-400">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} weight={i < t.rating ? "fill" : "regular"} />)}
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(t)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-brand-blue"><PencilSimple size={16} /></button>
                <button onClick={() => remove(t.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash size={16} /></button>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-700">"{t.text}"</p>
            <p className="mt-3 text-sm font-semibold text-slate-900">{t.author}</p>
            <p className="text-xs text-slate-500">{t.location}</p>
            <div className="mt-3 flex gap-2">
              {!t.published && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Hidden</span>}
              {t.is_sample && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Sample</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Testimonial</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Author</Label><Input data-testid="t-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Location</Label><Input data-testid="t-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Reading, PA" /></div>
            <div className="space-y-1.5"><Label>Rating (1-5)</Label><Input data-testid="t-rating" type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Review Text</Label><Textarea data-testid="t-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} /></div>
            <div className="flex items-center justify-between"><Label>Published (visible on site)</Label><Switch data-testid="t-published" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /></div>
            <div className="flex items-center justify-between"><Label>Mark as sample</Label><Switch data-testid="t-sample" checked={form.is_sample} onCheckedChange={(v) => setForm({ ...form, is_sample: v })} /></div>
            <Button onClick={save} data-testid="t-save" className="w-full rounded-full bg-brand-purple text-white hover:bg-brand-purple2">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
